import { type PropsWithChildren } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'
import { SELECTED_FILTERS_NO_DATA_MESSAGE } from 'constants/filters'
import { toast } from 'react-toastify'
import {
  PngExportProvider,
  usePngExport
} from '@jana-technology/platform-ui/charts'

import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import {
  IQRAGraphCsvExportParams,
  IQRARiskByCategoryGraphApiResult,
  IQRARiskBySystemGraphApiResult,
  useExportQRARiskByPipelineLengthGraphMutation,
  useExportQRARiskBySystemGraphMutation
} from 'app/services/api/qra/graphs'
import { useExportJobContext } from 'common/contexts/useExportJobContext'

import { GenericDonutChart } from 'features/RiskManagement/QRA/GraphsView/GenericDonutChart'

import { QRAGraphType } from '..'
import { getChartCsvExportQueryParams } from '../Implementations/Chart.helpers'

const EXPORT_UNAVAILABLE_MESSAGE =
  'Chart image export is unavailable right now.'

type ExportableDonutGraphType =
  | QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL
  | QRAGraphType.RISK_BY_SYSTEM

type ExportableGenericDonutChartProps = PropsWithChildren<{
  baselineAcceptanceCategory?: number[]
  csvExportParams?: IQRAGraphCsvExportParams
  data?: IQRARiskByCategoryGraphApiResult | IQRARiskBySystemGraphApiResult
  disableExportMenuItems?: boolean
  exportFileNameBase: string
  graphType: ExportableDonutGraphType
  showLoading: boolean
  title: string
  wisAcceptanceCategory?: number[]
  baselineSystemIds?: number[]
  scenarioSystemIds?: number[]
}>

const ExportableGenericDonutChartContent = ({
  baselineAcceptanceCategory,
  children,
  csvExportParams,
  data,
  disableExportMenuItems = false,
  exportFileNameBase,
  graphType,
  showLoading,
  title,
  wisAcceptanceCategory,
  baselineSystemIds,
  scenarioSystemIds
}: ExportableGenericDonutChartProps) => {
  const { assessmentId } = useParams()
  const { currentData: currentAssessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  const { currentData: baseline } = useBaselineInfoQuery('current')
  const { downloadImage, isReady: isChartPngExportReady } = usePngExport()
  const { createDataExportJob } = useExportJobContext()
  const [triggerExportRiskByPipelineLengthGraph] =
    useExportQRARiskByPipelineLengthGraphMutation()
  const [triggerExportRiskBySystemGraph] =
    useExportQRARiskBySystemGraphMutation()

  const shouldShowNoData =
    !showLoading && (data?.categoriesResult.length ?? 0) < 1

  const getFileNameForExport = () => {
    let fileName = exportFileNameBase

    if (currentAssessment?.name) {
      fileName += `_${currentAssessment.name}`
    }

    return fileName
  }

  const handleExportChartAsPng = async () => {
    const fileName = getFileNameForExport()

    if (!isChartPngExportReady) {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)

      return
    }

    try {
      await downloadImage({ fileName, options: { title } })
    } catch {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)
    }
  }

  const handleExportChartAsCsv = () => {
    const baselineId = baseline?.id
    const fileName = getFileNameForExport()

    if (csvExportParams) {
      createDataExportJob(
        graphType === QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL
          ? triggerExportRiskByPipelineLengthGraph
          : triggerExportRiskBySystemGraph,
        getChartCsvExportQueryParams({
          sortBy: 'default',
          graphType,
          csvExportParams,
          assessmentId,
          baselineId,
          baselineAcceptanceCategory,
          wisAcceptanceCategory,
          baselineSystemIds,
          scenarioSystemIds
        }),
        fileName,
        '.csv'
      )
    }
  }

  if (shouldShowNoData) {
    return (
      <GenericDonutChart showLoading={showLoading} title={title}>
        <div className='flex h-[393px] items-center justify-center text-center'>
          {SELECTED_FILTERS_NO_DATA_MESSAGE}
        </div>
      </GenericDonutChart>
    )
  }

  return (
    <GenericDonutChart
      showLoading={showLoading}
      title={title}
      actionMenuItems={[
        {
          children: 'Export chart as image',
          disabled: disableExportMenuItems,
          onSelect: () => {
            void handleExportChartAsPng()
          }
        },
        {
          children: 'Export chart as data',
          disabled: disableExportMenuItems,
          onSelect: () => handleExportChartAsCsv()
        }
      ]}
    >
      {children}
    </GenericDonutChart>
  )
}

export const ExportableGenericDonutChart = (
  props: ExportableGenericDonutChartProps
) => (
  <PngExportProvider>
    <ExportableGenericDonutChartContent {...props} />
  </PngExportProvider>
)
